import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { ScanFace, Trash2, Plus } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";
import { useAuth } from "../../lib/AuthContext";
import { faceIdSupported, registerFaceId, passkeyError, biometricName, clearLocalFaceId } from "../../lib/passkeys";

const PROMPT_KEY = "rosterly_faceid_prompted";
const usePasskeys = (enabled = true) => useQuery({ queryKey: ["passkeys"], queryFn: () => api.get("/passkeys").then((r) => r.data), enabled, staleTime: 60000 });
const useSupported = () => {
    const [ok, setOk] = useState(null);
    useEffect(() => { faceIdSupported().then(setOk); }, []);
    return ok;
};

// My Account card: enable on this device + list / remove saved passkeys.
export function FaceIdCard({ Card }) {
    const supported = useSupported();
    const qc = useQueryClient();
    const { data: keys = [] } = usePasskeys();
    const [busy, setBusy] = useState(false);
    const add = async () => {
        setBusy(true);
        try { await registerFaceId(); toast.success(`${biometricName()} sign-in enabled on this device`); qc.invalidateQueries({ queryKey: ["passkeys"] }); } catch (e) { toast.error(passkeyError(e)); }
        setBusy(false);
    };
    const remove = async (id) => {
        try { await api.delete(`/passkeys/${encodeURIComponent(id)}`); if (keys.filter((k) => k.rp_id === window.location.hostname).length <= 1) clearLocalFaceId(); qc.invalidateQueries({ queryKey: ["passkeys"] }); toast.success("Removed"); } catch (e) { toast.error(passkeyError(e)); }
    };
    return (
        <Card icon={ScanFace} title="Face ID Sign-In" testid="account-faceid-card">
            <p className="text-[12px] text-white/60 mb-1">Sign in with Face ID or Touch ID instead of Google. Enable it once on each device (while signed in); next time just tap "Sign in with Face ID".</p>
            <p className="text-[11px] text-white/45 mb-3">iPhone: Settings → [your name] → iCloud → Passwords &amp; Keychain must be ON, otherwise iOS asks for a QR code / security key.</p>
            <div className="space-y-1.5 mb-3" data-testid="faceid-passkey-list">
                {keys.length === 0 && <p className="text-[12px] text-white/45">No devices yet.</p>}
                {keys.map((k) => (
                    <div key={k.credential_id} className="flex items-center gap-2 rounded-lg bg-black/30 border border-white/10 px-3 py-2" data-testid={`faceid-key-${k.credential_id.slice(0, 8)}`}>
                        <ScanFace className="h-4 w-4 text-white/60" />
                        <div className="min-w-0">
                            <div className="text-[13px] font-semibold text-white truncate">{k.label}</div>
                            <div className="text-[11px] text-white/50">{k.rp_id} · added {String(k.created_at).slice(0, 10)}{k.last_used ? ` · last used ${String(k.last_used).slice(0, 10)}` : ""}</div>
                        </div>
                        <button type="button" onClick={() => remove(k.credential_id)} className="ml-auto h-8 w-8 inline-flex items-center justify-center rounded-md text-white/50 hover:text-[#EF4444] hover:bg-white/5" data-testid={`faceid-remove-${k.credential_id.slice(0, 8)}`}><Trash2 className="h-4 w-4" /></button>
                    </div>
                ))}
            </div>
            {supported ? (
                <button type="button" onClick={add} disabled={busy} data-testid="faceid-enable-btn"
                    className="inline-flex items-center gap-2 px-3.5 py-2 rounded-lg bg-white text-[#0A0B10] text-xs font-black uppercase tracking-wider disabled:opacity-60">
                    <Plus className="h-4 w-4" />{busy ? "Waiting…" : `Enable ${biometricName()} on this device`}
                </button>
            ) : supported === false ? <p className="text-[12px] text-white/45" data-testid="faceid-unsupported">This device or browser doesn't support Face ID / Touch ID sign-in.</p> : null}
        </Card>
    );
}

// One-time prompt after sign-in on a supported iPhone/iPad/Mac with no passkey yet.
export function FaceIdPrompt() {
    const { user } = useAuth();
    const supported = useSupported();
    const already = (() => { try { return !!localStorage.getItem(PROMPT_KEY); } catch { return true; } })();
    const { data: keys } = usePasskeys(!!user && supported === true && !already);
    const qc = useQueryClient();
    const [hide, setHide] = useState(false);
    const [busy, setBusy] = useState(false);
    const impersonating = (() => { try { return !!localStorage.getItem("rosterly_owner_token"); } catch { return false; } })();
    const host = window.location.hostname;
    const show = user && supported && !already && !hide && !impersonating && /iPhone|iPad|Macintosh/.test(navigator.userAgent) && keys && !keys.some((k) => k.rp_id === host);
    if (!show) return null;
    const done = () => { try { localStorage.setItem(PROMPT_KEY, "1"); } catch { /* noop */ } setHide(true); };
    const enable = async () => {
        setBusy(true);
        try { await registerFaceId(); toast.success(`${biometricName()} sign-in enabled`); qc.invalidateQueries({ queryKey: ["passkeys"] }); done(); } catch (e) { toast.error(passkeyError(e)); setBusy(false); }
    };
    return (
        <div className="fixed inset-x-3 bottom-24 sm:bottom-6 sm:left-auto sm:right-6 sm:w-[360px] z-[80] rounded-xl border border-white/15 bg-[#0E0F16] p-4 shadow-2xl shadow-black/60" data-testid="faceid-prompt">
            <div className="flex items-start gap-3">
                <ScanFace className="h-6 w-6 text-white shrink-0 mt-0.5" />
                <div>
                    <div className="text-sm font-black text-white">Enable {biometricName()} sign-in?</div>
                    <p className="text-[12px] text-white/60 mt-0.5">Next time, sign in with a glance instead of Google. You can turn it off in My Account.</p>
                </div>
            </div>
            <div className="flex gap-2 mt-3">
                <button type="button" onClick={enable} disabled={busy} data-testid="faceid-prompt-enable" className="flex-1 py-2 rounded-lg bg-white text-[#0A0B10] text-xs font-black uppercase tracking-wider disabled:opacity-60">{busy ? "Waiting…" : "Enable"}</button>
                <button type="button" onClick={done} data-testid="faceid-prompt-dismiss" className="flex-1 py-2 rounded-lg border border-white/15 text-white/70 text-xs font-black uppercase tracking-wider">Not now</button>
            </div>
        </div>
    );
}
